/**
 * Simplified Document User Related Context
 * Clean implementation using ProgressManager
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Annotation } from '../features/Annotation/types';
import { useDocumentContext } from './DocumentContext';
import { ReadingSessionInitResult } from '../services/ProgressManager';
import { ProgressData } from '../services/ProgressState';
import { useUserManagement } from '../features/UserManagement/components/UserManagementContext';
import ContinueReadingNotification from './ContinueReadingNotification';
import { TOCNode } from '../types/DocumentTypes';
import { useChapterNavigation } from '../services/useChapterNavigation';
import { useProgressTracking } from '../services/useProgressTracking';

type DocumentUserRelatedContextState = 'loading' | 'ready' | 'error';

type UserRelatedDocumentContextType = {
  state: DocumentUserRelatedContextState;
  progressData: ProgressData;
  annotations: Annotation[];
  updateAnnotation: (annotation: Annotation) => Annotation;
  removeAnnotation: (annotation: Annotation) => void;
  addAnnotation: (annotation: Annotation) => Annotation;
  trackProgress: () => void;
  initReadingSession: (
    chapters: TOCNode[],
    searchParams: URLSearchParams,
  ) => Promise<ReadingSessionInitResult>;
  // Add navigation callback support
  setMoveToChapter: (
    moveToChapter: (chapter: TOCNode) => Promise<void>,
  ) => void;
  navigateToChapterByNodeId: (nodeId: number) => Promise<void>;
  navigateToChapter: (chapter: TOCNode) => Promise<void>;
};

const DocumentUserRelatedContext =
  createContext<UserRelatedDocumentContextType | null>(null);

function getLocalStorageItemId(id: string) {
  return `alma-vkh-annotations-${id}`;
}

async function fetchAnnotations(id: string): Promise<Annotation[]> {
  const annotations = localStorage.getItem(getLocalStorageItemId(id));
  if (annotations) {
    return JSON.parse(annotations);
  }
  return [];
}

export function UserRelatedDocumentContextProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const { id, productMeta, chapters } = useDocumentContext();
  const { identity } = useUserManagement();

  const [state, setState] =
    useState<DocumentUserRelatedContextState>('loading');
  const [progressData, setProgressData] = useState<ProgressData>({
    currentBlock: null,
    totalBlocks: null,
    progressPercentage: 0,
  });
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  // Use the new progress tracking hook
  const {
    progressData: hookProgressData,
    modalState,
    trackProgress,
    initReadingSession,
    handleModalClose,
    handleContinueReading: hookHandleContinueReading,
  } = useProgressTracking(
    identity?.user?.sub ? productMeta?.isbn : undefined,
    identity?.actions?.getAccessToken ?? (async () => undefined),
    productMeta?.blockCount,
  );

  // Bridge hook state to local state where needed (to keep context shape stable)
  useEffect(() => {
    setProgressData(hookProgressData);
  }, [hookProgressData]);

  // Ensure tracking refresh on navigation
  const onNavigated = useCallback(() => {
    trackProgress();
  }, [trackProgress]);

  const {
    registerMoveToChapter,
    navigateToChapter: navToChapter,
    navigateToChapterByNodeId: navToNodeId,
  } = useChapterNavigation(chapters ?? undefined, onNavigated);

  const navigateToChapter = navToChapter;
  const navigateToChapterByNodeId = navToNodeId;

  // Load annotations
  useEffect(() => {
    if (productMeta) {
      fetchAnnotations(id).then((fetchedAnnotations) => {
        setAnnotations(fetchedAnnotations);
        setState('ready');
      });
    }
  }, [productMeta, id]);

  // Save annotations to localStorage
  useEffect(() => {
    localStorage.setItem(
      getLocalStorageItemId(id),
      JSON.stringify(annotations),
    );
  }, [annotations, id]);

  const addAnnotation = useCallback((annotation: Annotation): Annotation => {
    setAnnotations((prev) => [...prev, annotation]);
    return annotation;
  }, []);

  const updateAnnotation = useCallback((annotation: Annotation): Annotation => {
    setAnnotations((prev) =>
      prev.map((a) => (a.id === annotation.id ? annotation : a)),
    );
    return annotation;
  }, []);

  const removeAnnotation = useCallback((annotation: Annotation) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== annotation.id));
  }, []);

  // Register moveToChapter function from ReaderContext
  const setMoveToChapter = useCallback(
    (moveToChapter: (chapter: TOCNode) => Promise<void>) => {
      registerMoveToChapter(moveToChapter);
    },
    [registerMoveToChapter],
  );

  // Modal handlers
  const handleContinueReading = useCallback(async () => {
    if (!chapters) {
      return;
    }

    try {
      const result = await hookHandleContinueReading(chapters);
      await navToChapter(result.chapter);
      // Start or refresh tracking after user chose to continue
      trackProgress();
    } catch (_error) {
      // Silently handle errors - user can retry by clicking continue again
      console.info('Continue reading failed', _error);
    }
  }, [chapters, navToChapter, hookHandleContinueReading, trackProgress]);

  const contextValue = useMemo(
    () => ({
      state,
      progressData,
      annotations,
      updateAnnotation,
      removeAnnotation,
      addAnnotation,
      trackProgress,
      initReadingSession,
      setMoveToChapter,
      navigateToChapterByNodeId,
      navigateToChapter,
    }),
    [
      state,
      progressData,
      annotations,
      updateAnnotation,
      removeAnnotation,
      addAnnotation,
      trackProgress,
      initReadingSession,
      setMoveToChapter,
      navigateToChapterByNodeId,
      navigateToChapter,
    ],
  );

  return (
    <DocumentUserRelatedContext.Provider value={contextValue}>
      {children}
      <ContinueReadingNotification
        open={modalState.show}
        chapterTitle={modalState.data?.targetChapter?.title}
        onContinue={handleContinueReading}
        onClose={handleModalClose}
      />
    </DocumentUserRelatedContext.Provider>
  );
}

export function useUserRelatedDocumentContext() {
  const context = useContext(DocumentUserRelatedContext);
  if (!context) {
    throw new Error(
      'useUserRelatedDocumentContext must be used within a UserRelatedDocumentContextProvider',
    );
  }

  return context;
}
